// src/encounter/services/use-malaffi-sso.ts
// Malaffi HIE SSO — the popup lifecycle. React port of Flutter `WebTabManager`
// (web_tab_manager.dart) as used by the encounter screen:
//   · openPopup(url)  : closes any previously-opened popup, then window.open(url, 'popup',
//                       'width=800,height=600,resizable,scrollbars=no,status=no')  (:53-61)
//   · closePopup()    : called from `dispose()` (encounter_screen.dart:236) — leaving the
//                       encounter closes the Malaffi window
//   · beforeunload    : `_addRefreshListener` (:37-44) closes it on page refresh
//
// Difference from Flutter, deliberately: Flutter re-registers the beforeunload listener on EVERY
// open and never removes it (a listener leak). Here it is registered once per mount and removed on
// unmount — same behaviour, no leak.
//
// VISIBILITY vs. CLICK — these are two different gates, and conflating them was a real parity bug
// (first cut of this hook, caught on Andrew's dev box 2026-08-05: Flutter showed the tab, React did
// not). Flutter decides the TAB purely on the org flag — ui_data.dart:130 tests
// `integrationType == 'malaffi'` and nothing else; the MRN and provider id are read later, at click
// time, and Flutter will happily interpolate nulls into the URL. So:
//   · visible  ⟺ the org's integrationType is Malaffi          (Flutter parity, org-level truth)
//   · clickable⟺ ALSO have MRN + user id + a configured base   (checked at click, not at render)
// Gating VISIBILITY on the MRN too made the tab vanish whenever the patient detail hadn't resolved,
// or the environment had no SSO base — i.e. a tab Flutter shows, React silently hid. We keep the
// safety property that matters (never open an SSO hand-off with a blank patient) by failing the
// CLICK loudly instead of hiding the affordance.
import { useCallback, useEffect, useRef } from 'react';
import { env } from '@/config/env';
import { showWarning } from '@/shared/lib/toast-helpers';
import {
  buildMalaffiSsoUrl,
  isMalaffiIntegration,
  MALAFFI_POPUP_FEATURES,
  MALAFFI_POPUP_NAME,
} from '../lib/malaffi';

export interface UseMalaffiSsoParams {
  /** `user.resourceBasedIntegration.integrationType` — the org's HIE, from GetUserDataToken. */
  integrationType?: string | null;
  /** Patient MRN → the `q1` SSO param. */
  mrn?: string | null;
  /** Logged-in user id → the `q2` SSO param (Flutter `providerId()` = LoginUserData.id). */
  providerId?: string | null;
}

/**
 * The Malaffi SSO opener, or `undefined` when the ORGANIZATION is not on Malaffi — the same single
 * condition Flutter gates its third tab on. A Malaffi org always gets the opener (and therefore the
 * tab); if the MRN / user id / SSO base can't produce a URL, the click reports why instead of
 * opening a hand-off pointed at no patient.
 */
export function useMalaffiSso({
  integrationType,
  mrn,
  providerId,
}: UseMalaffiSsoParams): (() => void) | undefined {
  const popupRef = useRef<Window | null>(null);

  const isMalaffiOrg = isMalaffiIntegration(integrationType);
  const url = isMalaffiOrg
    ? buildMalaffiSsoUrl({ baseUrl: env.malaffiSsoUrl, mrn, providerId })
    : null;

  const closePopup = useCallback(() => {
    const w = popupRef.current;
    popupRef.current = null;
    if (!w || w.closed) return;
    try {
      w.close();
    } catch {
      // Cross-origin/already-gone window — nothing to do (Flutter swallows the same failure).
    }
  }, []);

  // Close on page refresh AND on unmount (leaving the encounter) — Flutter's beforeunload
  // listener + `dispose()`. A Malaffi session must not outlive the encounter that opened it:
  // it is an authenticated view of THIS patient's external records.
  useEffect(() => {
    window.addEventListener('beforeunload', closePopup);
    return () => {
      window.removeEventListener('beforeunload', closePopup);
      closePopup();
    };
  }, [closePopup]);

  const open = useCallback(() => {
    if (!url) {
      // The org IS on Malaffi but something needed for the hand-off is missing. Name it — a tab
      // that silently does nothing is the worst of the three options (hide / no-op / explain).
      const missing = [
        !env.malaffiSsoUrl?.trim() && 'the Malaffi SSO address for this environment',
        !mrn?.trim() && "the patient's MRN",
        !providerId?.trim() && 'your user id',
      ].filter(Boolean) as string[];
      showWarning(`Can’t open Malaffi — missing ${missing.join(' and ')}.`);
      return;
    }
    closePopup(); // openPopup() closes the previous one first
    popupRef.current = window.open(url, MALAFFI_POPUP_NAME, MALAFFI_POPUP_FEATURES);
  }, [url, mrn, providerId, closePopup]);

  return isMalaffiOrg ? open : undefined;
}
